export interface DataKitDto {
  id?: string;
  teamId?: string;
  name: string;
  description: string;
  archived: boolean;
  itemCount: number;
  locationCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitPriceOverrideDto {
  id?: string;
  unitType: string;
  unitId: string;
  price: string;
}

export interface DataKitMenuPriceDto {
  menuId: string;
  price: string;
}

export interface DataKitItemPriceDto {
  id?: string;
  size: string;
  price: string;
  sequence: number;
  overrides: DataKitPriceOverrideDto[];
  /** Per-menu prices for this size. Read-only here; writes go through the menu-price route. */
  menuOverrides?: DataKitMenuPriceDto[];
}

export interface DataKitItemGroupDto {
  groupName: string;
  /** Full ancestry, root first: ["Prime Seafood", "Raw"]. Replaced subgroupName (Sept 16). */
  groupPath: string[];
}

export interface DataKitMenuDto {
  id?: string;
  name: string;
  note?: string | null;
  designCount: number;
  itemCount: number;
  locationCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface MenuDesignDto {
  designId: string;
  designName: string;
  locationId?: number | null;
  menuId?: string | null;
  menuName?: string | null;
}

export interface MenuRequestDto {
  name: string;
  note?: string | null;
}

export interface DesignMenuRequestDto {
  menuId?: string | null;
}

export interface MenuPriceRequestDto {
  menuId: string;
  size: string;
  price?: string | null;
}

export interface VenueMenuPriceDto {
  menuId: string;
  menuName: string;
  price: string;
}

export interface ReparentGroupRequestDto {
  path: string[];
  newParentPath: string[];
}

export interface SubgroupRequestDto {
  groupName: string;
  name: string;
  /** Full path to create, root first. Falls back to name as one level when empty. */
  path: string[];
}

export interface DataKitItemModifierDto {
  id?: string;
  name: string;
  price?: string | null;
  sequence: number;
}

export interface DataKitItemModifierGroupDto {
  id?: string;
  name: string;
  sequence: number;
  modifiers: DataKitItemModifierDto[];
}

export interface DataKitSourceEventDto {
  id?: string;
  kind: string;
  fileName?: string | null;
  occurredDate?: string;
}

export interface DataKitItemDto {
  id?: string;
  externalId?: string;
  dataKitId?: string;
  teamId?: string;
  name: string;
  description: string;
  graphicRef?: string | null;
  sequence: number;
  archived: boolean;
  available: boolean;
  extras?: string | null;
  prices: DataKitItemPriceDto[];
  tags: string[];
  groups: DataKitItemGroupDto[];
  modifierGroups: DataKitItemModifierGroupDto[];
  sourceEvent?: DataKitSourceEventDto | null;
  usedOnCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitGroupDto {
  id?: string;
  /** Full ancestry, root first, when the server sends it. */
  path?: string[];
  dataKitId?: string;
  name: string;
  note?: string | null;
  itemCount: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface DataKitTagDto {
  id?: string;
  dataKitId?: string;
  name: string;
  itemCount: number;
  createdDate?: string;
  updatedDate?: string;
}

/** What deleting a kit would reach - every location it's on, and how many designs there use it. */
export interface KitDeletePreviewDto {
  kitId: string;
  itemCount: number;
  locations: { unitType: string; unitId: string; designCount: number }[];
}

export interface DataKitDeleteResultDto {
  itemsDeleted: number;
  locationsRemoved: number;
  designsUnlinked: number;
}

export interface DataKitAssignmentDto {
  id?: string;
  dataKitId?: string;
  unitType: string;
  unitId: string;
}

export interface MembershipRequestDto {
  groupName: string;
}

export interface SetAvailabilityRequestDto {
  itemIds: string[];
  available: boolean;
}

export interface BulkItemIdsRequestDto {
  itemIds: string[];
}

export interface RenameGroupRequestDto {
  name: string;
  newName: string;
  path: string[];
}

export interface GroupNoteRequestDto {
  name: string;
  note?: string | null;
  path: string[];
}

export interface RenameTagRequestDto {
  name: string;
  newName: string;
}

export interface MergeTagsRequestDto {
  from: string;
  into: string;
}

export interface MenuUsageDto {
  /** The account menu's id, or null for the unlabelled bucket. */
  menuId?: string | null;
  menuLabel?: string | null;
  designCount: number;
  /** Items of this kit the menu's designs place. */
  itemCount: number;
}

export interface GroupMenuUsageDto {
  groupName: string;
  menuLabels: (string | null)[];
}

export interface UnassignPreviewDto {
  kitId: string;
  unitType: string;
  unitId: string;
  itemCount: number;
  designCount: number;
  placedItemCount: number;
}

export interface DataKitItemDiffDto {
  itemId: string;
  externalId: string;
  name: string;
  new: boolean;
  changedFields: string[];
}

export interface DataKitGroupDiffDto {
  groupId: string;
  name: string;
  new: boolean;
  changedFields: string[];
}

export interface DataKitPublishDiffDto {
  /** itemCount + groupCount - items and groups, not fields. */
  totalChanges: number;
  /** Items on a design with at least one change waiting. */
  itemCount?: number;
  /** Groups a design shows whose own name, note or position has a change waiting. */
  groupCount?: number;
  /** Distinct designs that will change when this publishes. */
  designCount?: number;
  items: DataKitItemDiffDto[];
  groups: DataKitGroupDiffDto[];
}

export interface DataKitPublishResultDto {
  itemsPublished: number;
  groupsPublished: number;
}

export interface DataKitDiscardResultDto {
  itemsReverted: number;
  itemsDeleted: number;
  groupsReverted: number;
}

export interface DataKitPublishEventDto {
  id?: string;
  designId: string;
  itemExternalId: string;
  field: string;
  oldValue?: string | null;
  newValue?: string | null;
  applied: boolean;
  publishedDate?: string;
}

export interface VenuePriceDto {
  size: string;
  price: string;
  overridden: boolean;
  /** Menus that price this size themselves - drives the "every menu except Dinner" chip. */
  menuPrices: VenueMenuPriceDto[];
}

export interface VenueGroupEntryDto {
  groupName: string;
  groupNote: string;
  /** Full published ancestry, root first - one design header per element. */
  groupPath: string[];
}

export interface VenueItemDto {
  externalId: string;
  dataKitId: string;
  kitName: string;
  name: string;
  description: string;
  groupName: string;
  groupNote: string;
  groups: VenueGroupEntryDto[];
  tags: string[];
  graphicRef?: string | null;
  prices: VenuePriceDto[];
  available: boolean;
}

export interface ColumnSuggestion {
  columnIndex: number;
  header: string;
  target: string;
}

export interface ImportParseDto {
  fileName: string;
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
  suggestedMapping: ColumnSuggestion[];
  detectedPairs: number;
}

export interface ImportMappingDto {
  columnIndex: number;
  target: string;
  pairIndex?: number | null;
}

export interface ImportRowPriceDto {
  size: string;
  price: string;
}

export interface ImportRowModifierDto {
  name: string;
  price?: string | null;
}

export interface ImportRowModifierGroupDto {
  name: string;
  modifiers: ImportRowModifierDto[];
}

export interface ImportRowDto {
  rowNumber: number;
  name: string;
  /** The leaf group the row is filed in. */
  groupName: string;
  /** Full ancestry, root first. Empty falls back to [groupName]. */
  groupPath?: string[];
  /**
   * Further groups the same item is filed in - set by the server when it folds rows sharing an
   * external id and differing only by group into one item.
   */
  alsoInGroupPaths?: string[][];
  /** Rows folded into this one, so skipping it can drop them as well. */
  foldedRowNumbers?: number[];
  description?: string | null;
  extras?: string | null;
  tags?: string[] | null;
  externalId: string;
  prices?: ImportRowPriceDto[] | null;
  /** null when no modifier column was mapped; an empty array means the member cleared them. */
  modifierGroups?: ImportRowModifierGroupDto[] | null;
  matchedItemId?: string | null;
}

/** What kind of decision an ambiguous row is - see shared's `ImportAmbiguousRowDto.kind`. */
export type ImportDecisionKind =
  | "FILE_DUPLICATE"
  | "KIT_MATCH"
  | "EXTERNAL_ID_CONFLICT"
  | "ARCHIVED_ID"
  | "UPDATE_COLLISION";

export interface ImportAmbiguousRowDto {
  row: ImportRowDto;
  reason: string;
  candidateItemIds: string[];
  /** True when this row can be resolved by adding it as its own new item. */
  canCreateSeparately?: boolean;
  kind?: ImportDecisionKind;
  /** Rows sharing this key are one decision card. */
  setKey?: string;
  /** FILE_DUPLICATE only: what keeping the row does. */
  outcome?: "NEW" | "UPDATE" | "UP_TO_DATE" | "CHOOSE" | null;
}

/** The `resolutions` value meaning "add this row as a separate new item". */
export const IMPORT_RESOLUTION_CREATE = "__create__";
/** The `resolutions` value meaning the member chose to skip this row. */
export const IMPORT_RESOLUTION_SKIP = "__skip__";
/** The `resolutions` value meaning "keep this row as it is" - a FILE_DUPLICATE row's default. */
export const IMPORT_RESOLUTION_KEEP = "__keep__";

export interface SkippedRowDto {
  rowNumber: number;
  reason: string;
}

export interface DataKitImportRequestDto {
  headers: string[];
  rows: string[][];
  rowNumbers: number[];
  mapping: ImportMappingDto[];
  fileName: string;
  resolutions: Record<number, string>;
}

export interface DataKitImportPreviewDto {
  creates: ImportRowDto[];
  updates: ImportRowDto[];
  ambiguous: ImportAmbiguousRowDto[];
  /** Matched rows that would change nothing - counted, not imported again. */
  alreadyUpToDate?: ImportRowDto[];
  /** Byte-identical repeats of an earlier row, collapsed before matching. */
  mergedDuplicates?: ImportRowDto[];
  skipped: SkippedRowDto[];
}

export interface DataKitImportResultResponseDto {
  created: number;
  updated: number;
  skipped: number;
  ambiguous: number;
  /** Rows the member chose to skip in the review step. */
  skippedByChoice?: number;
  ambiguousRows: ImportAmbiguousRowDto[];
  skippedRows: SkippedRowDto[];
}

export interface VenuePriceOverrideRequestDto {
  size: string;
  price?: string | null;
}

export interface FinalMenuDto {
  designId: string;
  designName: string;
  menuLabel?: string | null;
  locationId?: number | null;
  locationName?: string | null;
  itemCount: number;
}

export interface FinalMenuPageDto {
  rows: FinalMenuDto[];
  total: number;
  limit: number;
  offset: number;
}
